import React from 'react';
import { UserRound, Utensils } from 'lucide-react';
import type { AppNotification } from '@/lib/api/notifications';
import biomarkerIcon from '@/assets/images/benzene-ring.svg';
import notificationAppointmentIcon from '@/assets/images/notifications/calendar.svg';
import notificationBellIcon from '@/assets/images/notifications/bell.svg';
import notificationDocumentIcon from '@/assets/images/notifications/document-edit.svg';
import notificationPaymentIcon from '@/assets/images/notifications/dollar-minimalistic.svg';

type NotificationCategory =
  | 'payment'
  | 'appointment'
  | 'assessment'
  | 'biomarker'
  | 'client'
  | 'mealPlan'
  | 'message'
  | 'default';

function normalizeParamValue(value: unknown): string {
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return '';
}

function getParam(notification: AppNotification, key: string): string {
  const value = notification.cta?.params?.[key] ?? notification.metadata?.[key];
  return normalizeParamValue(value);
}

function getFirstParam(notification: AppNotification, keys: string[]): string {
  for (const key of keys) {
    const value = getParam(notification, key);
    if (value) return value;
  }
  return '';
}

function getQuestionnaireInstanceId(notification: AppNotification): string {
  return (
    getFirstParam(notification, [
      'instanceId',
      'questionnaireInstanceId',
      'questionnaireResponseId',
      'responseInstanceId',
      'responseId',
    ]) ||
    (notification.source?.type === 'questionnaire_instance'
      ? normalizeParamValue(notification.source.id)
      : '')
  );
}

function isQuestionnaireResponseNotification(notification: AppNotification, destination: string): boolean {
  const text = [
    notification.eventKey,
    notification.workflow,
    notification.title,
    notification.body,
    notification.cta?.label,
    destination,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  return /(completed|submitted|response|responses|review)/.test(text);
}

function isPractitionerQuestionnaireAssignmentNotification(
  notification: AppNotification,
  destination: string
): boolean {
  if (notification.recipientType === 'client') return false;

  const text = [
    notification.eventKey,
    notification.workflow,
    notification.title,
    notification.body,
    notification.cta?.label,
    destination,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  return (
    notification.eventKey === 'questionnaire_assigned' ||
    (destination === 'questionnaire_practitioner' && /(assigned|assignment)/.test(text))
  );
}

function isQuestionnaireNotification(notification: AppNotification, destination: string): boolean {
  const text = [
    destination,
    notification.eventKey,
    notification.workflow,
    notification.title,
    notification.body,
    notification.cta?.label,
    notification.source?.type,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  return /(questionnaire|assessment|form)/.test(text);
}

function withQuery(path: string, params: Record<string, string>): string {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value.trim()) query.set(key, value.trim());
  }
  const queryString = query.toString();
  return queryString ? `${path}?${queryString}` : path;
}

export function getNotificationPath(notification: AppNotification): string {
  const destination = notification.cta?.destination || '';
  const clientId = getParam(notification, 'clientId');
  const appointmentId = getParam(notification, 'appointmentId');
  const mealPlanId = getParam(notification, 'mealPlanId');
  const questionnaireId = getParam(notification, 'questionnaireId');

  if (
    destination === 'client_pending_deletion' ||
    notification.eventKey === 'client_account_deletion_requested'
  ) {
    return withQuery('/clients', {
      status: 'pending_deletion',
      focusClientId: clientId,
    });
  }

  if (
    destination === 'client_overview' ||
    notification.eventKey === 'client_account_deletion_restored'
  ) {
    return clientId
      ? `/clients/${encodeURIComponent(clientId)}/details-v2/overview`
      : '/clients';
  }

  if (destination.includes('appointment')) {
    if (clientId && appointmentId) {
      return `/clients/${encodeURIComponent(clientId)}/details-v2/appointments?session=${encodeURIComponent(appointmentId)}`;
    }
    return '/appointments';
  }

  if (destination.includes('blood_report')) {
    return clientId ? `/clients/${encodeURIComponent(clientId)}/details-v2/lab-reports` : '/clients';
  }

  if (destination.includes('meal_plan')) {
    if (clientId && mealPlanId) {
      return `/meal-plans/${encodeURIComponent(mealPlanId)}/clients/${encodeURIComponent(clientId)}/nutrition/1`;
    }
    return clientId ? `/clients/${encodeURIComponent(clientId)}/details-v2/meal-plan` : '/meal-planning';
  }

  if (isQuestionnaireNotification(notification, destination)) {
    const instanceId = getQuestionnaireInstanceId(notification);
    const clientName = getFirstParam(notification, ['clientName', 'clientFullName']);
    if (clientId && isPractitionerQuestionnaireAssignmentNotification(notification, destination)) {
      return withQuery(`/clients/${encodeURIComponent(clientId)}/details-v2/questionnaires`, {
        fromPage: getFirstParam(notification, ['fromPage', 'page']) || '1',
        fromLimit: getFirstParam(notification, ['fromLimit', 'limit']) || '10',
        fromStatus: getParam(notification, 'fromStatus'),
        fromView: getParam(notification, 'fromView'),
        questionnaireStatus: 'pending',
      });
    }
    if (questionnaireId && instanceId && isQuestionnaireResponseNotification(notification, destination)) {
      return withQuery(
        `/questionnaires/${encodeURIComponent(questionnaireId)}/responses/${encodeURIComponent(instanceId)}`,
        { clientId, clientName }
      );
    }
    if (questionnaireId) {
      return `/questionnaires/${encodeURIComponent(questionnaireId)}/responses`;
    }
    return '/questionnaires';
  }

  if (destination.includes('message')) {
    return '/dashboard';
  }

  return '/dashboard';
}

export function formatNotificationTimestamp(value: string | null): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  const day = date.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
  const time = date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  return `${day} | ${time}`;
}

export function getNotificationSearchText(notification: AppNotification): string {
  return [
    notification.eventKey,
    notification.workflow,
    notification.title,
    notification.body,
    notification.cta?.destination,
    notification.source?.type,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}

function getNotificationCategoryText(notification: AppNotification, includeBody = false): string {
  return [
    notification.eventKey,
    notification.workflow,
    notification.title,
    notification.cta?.destination,
    notification.cta?.label,
    notification.source?.type,
    includeBody ? notification.body : '',
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}

function getNotificationCategory(notification: AppNotification): NotificationCategory {
  const structuredText = getNotificationCategoryText(notification);

  if (/(appointment|session|schedule|booking|calendar)/.test(structuredText)) {
    return 'appointment';
  }
  if (/(questionnaire|assessment|form|response)/.test(structuredText)) {
    return 'assessment';
  }
  if (/(invoice|billing|payment|paid|overdue|renewal|subscription)/.test(structuredText)) {
    return 'payment';
  }
  if (/(lab|blood|biomarker)/.test(structuredText)) {
    return 'biomarker';
  }
  if (/(meal|recipe|nutrition|diet)/.test(structuredText)) {
    return 'mealPlan';
  }
  if (/(client|lead|prospect|user|profile)/.test(structuredText)) {
    return 'client';
  }
  if (/(message|chat|comment)/.test(structuredText)) {
    return 'message';
  }

  const fallbackText = getNotificationCategoryText(notification, true);

  if (/(appointment|session|schedule|booking|calendar)/.test(fallbackText)) {
    return 'appointment';
  }
  if (/(questionnaire|assessment|form|response)/.test(fallbackText)) {
    return 'assessment';
  }
  if (/(invoice|billing|payment|paid|overdue|renewal|subscription)/.test(fallbackText)) {
    return 'payment';
  }
  if (/(lab|blood|biomarker)/.test(fallbackText)) {
    return 'biomarker';
  }
  if (/(meal|recipe|nutrition|diet)/.test(fallbackText)) {
    return 'mealPlan';
  }
  if (/(client|lead|prospect|user|profile)/.test(fallbackText)) {
    return 'client';
  }
  if (/(message|chat|comment)/.test(fallbackText)) {
    return 'message';
  }
  return 'default';
}

export function NotificationBellAsset({ className }: { className?: string }) {
  return <img src={notificationBellIcon} alt="" className={className} aria-hidden="true" />;
}

export function NotificationCategoryIcon({
  notification,
  className = 'h-[46px] w-[46px] rounded-[10px]',
  iconClassName = 'h-[22px] w-[22px]',
  assessmentIconClassName = 'h-[28px] w-[28px]',
  biomarkerIconClassName = 'h-[32px] w-[32px]',
}: {
  notification: AppNotification;
  className?: string;
  iconClassName?: string;
  assessmentIconClassName?: string;
  biomarkerIconClassName?: string;
}) {
  const category = getNotificationCategory(notification);

  if (category === 'payment') {
    return (
      <span className={`inline-flex shrink-0 items-center justify-center bg-[#FCE8EA] ${className}`}>
        <img src={notificationPaymentIcon} alt="" className={iconClassName} aria-hidden="true" />
      </span>
    );
  }

  if (category === 'appointment') {
    return (
      <span className={`inline-flex shrink-0 items-center justify-center bg-[#DDF7FC] ${className}`}>
        <img src={notificationAppointmentIcon} alt="" className={iconClassName} aria-hidden="true" />
      </span>
    );
  }

  if (category === 'assessment') {
    return (
      <span className={`inline-flex shrink-0 items-center justify-center bg-[#F1F2F4] ${className}`}>
        <img src={notificationDocumentIcon} alt="" className={assessmentIconClassName} aria-hidden="true" />
      </span>
    );
  }

  if (category === 'biomarker') {
    return (
      <span className={`inline-flex shrink-0 items-center justify-center bg-[#E8F6FE] ${className}`}>
        <img src={biomarkerIcon} alt="" className={biomarkerIconClassName} aria-hidden="true" />
      </span>
    );
  }

  if (category === 'client') {
    return (
      <span className={`inline-flex shrink-0 items-center justify-center bg-[#FEF3DE] text-[#F5A623] ${className}`}>
        <UserRound className={iconClassName} strokeWidth={2.4} aria-hidden="true" />
      </span>
    );
  }

  if (category === 'mealPlan') {
    return (
      <span className={`inline-flex shrink-0 items-center justify-center bg-[#E7F8F4] text-[#13B8A6] ${className}`}>
        <Utensils className={iconClassName} strokeWidth={2.3} aria-hidden="true" />
      </span>
    );
  }

  return (
    <span className={`inline-flex shrink-0 items-center justify-center bg-[#E7F6FB] text-[#20AEDC] ${className}`}>
      <NotificationBellAsset className={iconClassName} />
    </span>
  );
}
